import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { COLORS, BRAND } from '@/lib/brand';
import { motion } from 'framer-motion';
import { Loader2, Lock, User, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(username, password);
      toast.success('¡Bienvenido!');
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'bodega') {
        navigate('/bodega');
      } else if (user.role === 'logistica') {
        navigate('/logistica');
      } else if (user.role === 'inventarista') {
        navigate('/inventarista');
      } else {
        navigate('/local');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4" 
      style={{ backgroundColor: COLORS.background.DEFAULT }}
      data-testid="login-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div 
          className="bg-white rounded-2xl p-8 shadow-xl"
          style={{ border: `1px solid ${COLORS.border.DEFAULT}` }}
        >
          {/* Branding Header */}
          <div className="text-center mb-8">
            {/* Símbolo de caja con fondo verde TUPU */}
            <div className="flex justify-center mb-5">
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg"
                style={{ backgroundColor: COLORS.brand.green }}
                data-testid="login-box-icon"
              >
                <Package className="w-9 h-9 text-white" />
              </div>
            </div>
            
            {/* Logo TUPU + Stock */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <img 
                src="/images/tupu-logo.png"
                alt="TUPU"
                className="h-12 w-auto object-contain"
                onError={(e) => { 
                  // Fallback: mostrar texto si no carga imagen
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                data-testid="login-tupu-logo"
              />
              <span 
                className="text-4xl font-bold hidden items-center"
                style={{ color: COLORS.brand.green }}
              >
                TUPU
              </span>
              <span 
                className="text-4xl font-bold"
                style={{ color: COLORS.primary.DEFAULT }}
                data-testid="login-stock-text"
              >
                Stock
              </span>
            </div>
            
            {/* Descriptor */}
            <p 
              className="text-sm font-medium"
              style={{ color: COLORS.text.secondary }}
              data-testid="login-tagline"
            >
              {BRAND.tagline}
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label 
                htmlFor="username" 
                className="text-sm font-medium"
                style={{ color: COLORS.text.primary }}
              >
                Usuario
              </Label>
              <div className="relative mt-2">
                <User 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" 
                  style={{ color: COLORS.text.muted }} 
                />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 h-12 rounded-lg"
                  placeholder="Ingresa tu usuario"
                  required
                  data-testid="login-username-input"
                  style={{ 
                    borderColor: COLORS.border.DEFAULT,
                    color: COLORS.text.primary
                  }}
                />
              </div>
            </div>

            <div>
              <Label 
                htmlFor="password" 
                className="text-sm font-medium"
                style={{ color: COLORS.text.primary }}
              >
                Contraseña
              </Label>
              <div className="relative mt-2">
                <Lock 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" 
                  style={{ color: COLORS.text.muted }} 
                />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 rounded-lg"
                  placeholder="Ingresa tu contraseña"
                  required
                  data-testid="login-password-input"
                  style={{ 
                    borderColor: COLORS.border.DEFAULT,
                    color: COLORS.text.primary
                  }}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-lg text-base font-semibold transition-all shadow-md hover:shadow-lg"
              disabled={loading}
              data-testid="login-submit-button"
              style={{ 
                backgroundColor: COLORS.primary.DEFAULT, 
                color: '#FFFFFF'
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = COLORS.primary.hover;
              }}
              onMouseLeave={(e) => {
                if (!loading) e.currentTarget.style.backgroundColor = COLORS.primary.DEFAULT;
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Ingresando...
                </>
              ) : (
                'Ingresar'
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6" style={{ borderTop: `1px solid ${COLORS.border.DEFAULT}` }}>
            <p 
              className="text-xs text-center font-medium"
              style={{ color: COLORS.text.muted }}
            >
              {BRAND.name} v{BRAND.version}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
